<template>
    <div>
        <!-- Loading Spinner -->
        <div v-if="loading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-12">
            <div class="ppv2-text-center">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-12 ppv2-w-12 ppv2-border-b-4 ppv2-border-yellow-500 ppv2-mx-auto"
                ></div>
                <span class="ppv2-mt-3 ppv2-block ppv2-text-sm ppv2-text-gray-600">{{ $t('invoices.loading_orders') }}</span>
            </div>
        </div>

        <!-- DataTable -->
        <data-table
            v-else
            ref="dataTable"
            :items="orders"
            :columns="columns"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :perPage="perPage"
            :total="total"
            :enableMultiSelect="false"
            :enableDragDrop="false"
            @page-changed="handlePageChange"
        >
            <!-- Custom cell: Order ID -->
            <template #column-id="{ item }">
                <a :href="`/back-office/commandes/${item.id}/descriptif`" class="ppv2-text-black hover:ppv2-underline">
                    #{{ item.id }}
                </a>
            </template>

            <!-- Custom cell: Customer -->
            <template #column-customer="{ item }">
                <span
                    v-if="item.user?.id && !isWalkInCustomer(item.user)"
                    @click.prevent="openCustomerModal(item.user.id)"
                    class="hover:ppv2-underline"
                >
                    {{ item.user.first_name }} {{ item.user.last_name }}
                </span>
                <span v-else class="ppv2-text-black ppv2-text-opacity-70" :style="{ fontSize: normalizeFontSize(13) }">{{
                    $t('invoices.walk_in_customer')
                }}</span>
            </template>

            <!-- Custom cell: Articles -->
            <template #column-articles="{ item }">
                <div v-if="item.order_summary">
                    <div class="poppinsMedium">{{ item.order_summary.article_display || '-' }}</div>
                    <div v-if="item.order_summary.next_activity_date" class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-70">
                        {{ item.order_summary.next_activity_date }}
                    </div>
                </div>
                <div v-else-if="item.items && item.items.length > 0">
                    <span class="poppinsMedium">{{ item.items.length }}</span> {{ $t('invoices.articles') }}
                    <div class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-70">
                        {{
                            item.items
                                .slice(0, 2)
                                .map((i) => i.itemable?.name || $t('invoices.article'))
                                .join(', ')
                        }}
                        <span v-if="item.items.length > 2">...</span>
                    </div>
                </div>
                <span v-else class="ppv2-text-black ppv2-text-opacity-70">-</span>
            </template>

            <!-- Custom cell: Total Price -->
            <template #column-total_price="{ item }">
                <span class="poppinsMedium">{{ formatCurrency(item.total_price) }}</span>
            </template>

            <!-- Custom cell: Status -->
            <template #column-status="{ item }">
                <span
                    :class="getStatusClass(item)"
                    class="ppv2-inline-flex ppv2-items-center ppv2-px-2 ppv2-py-1 ppv2-rounded-full ppv2-text-xs poppinsMedium"
                >
                    {{ getStatusLabel(item) }}
                </span>
            </template>

            <!-- Custom cell: Date -->
            <template #column-created_at="{ item }">
                <span>{{ formatDate(item.created_at) }}</span>
            </template>

            <!-- Custom cell: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        type="button"
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                        :title="$t('common.actions')"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Voir la commande -->
                        <button
                            type="button"
                            @click="onMenuAction('view', item)"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <Eye :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('invoices.view_order')
                            }}</span>
                        </button>

                        <!-- Modifier la commande -->
                        <button
                            v-if="canModifyOrder && isPending(item)"
                            type="button"
                            @click="onMenuAction('modify', item)"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <SquarePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('invoices.modify_order')
                            }}</span>
                        </button>

                        <!-- Encaisser la commande -->
                        <button
                            v-if="canModifyOrder && isPending(item)"
                            type="button"
                            @click="onMenuAction('encaisser', item)"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <Computer :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('invoices.encaisser_order')
                            }}</span>
                        </button>

                        <!-- Créer une facture pro forma -->
                        <button
                            v-if="canModifyOrder && isPending(item)"
                            type="button"
                            @click="onMenuAction('proforma', item)"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <FilePlus2 :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('invoices.create_proforma')
                            }}</span>
                        </button>

                        <!-- Annuler -->
                        <button
                            v-if="canDeleteOrder && !isCancelled(item)"
                            type="button"
                            @click="onMenuAction('cancel', item)"
                            class="actionItem trash ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <XCircle :size="16" class="hover:ppv2-text-red-500" />
                            <span class="poppinsRegular hover:ppv2-text-red-500" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('invoices.cancel_order')
                            }}</span>
                        </button>
                    </div>
                </div>
            </template>
        </data-table>

        <order-actions-dialogs ref="orderActionsDialogs" @action-completed="fetchOrders" />
    </div>
</template>

<script>
import DataTable from '../Shared/DataTable.vue'
import { eventBus } from '../Customer/eventBus'
import { MoreVertical, Eye, SquarePen, Computer, FilePlus2, XCircle } from 'lucide-vue'
import moment from 'moment'
import normalizeSize from '../../utils/normalizeSize'
import normalizeFontSize from '../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../utils/perPage'
import OrderActionsDialogs from '../Orders/OrderActionsDialogs.vue'

export default {
    name: 'DraftOrdersDataTable',
    components: {
        DataTable,
        OrderActionsDialogs,
        MoreVertical,
        Eye,
        SquarePen,
        Computer,
        FilePlus2,
        XCircle,
    },
    props: {
        startDate: {
            type: String,
            required: true,
        },
        endDate: {
            type: String,
            required: true,
        },
        searchQuery: {
            type: String,
            default: '',
        },
        filters: {
            // NEW: Advanced filters
            type: Object,
            default: () => ({}),
        },
        canModifyOrder: {
            type: Boolean,
            default: false,
        },
        canDeleteOrder: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            orders: [],
            loading: false,
            currentPage: 1,
            totalPages: 1,
            perPage: getPerPage('invoices_en_attente_items_per_page', 25),
            total: 0,
            localSearchQuery: '',
            fetchTimer: null,
            activeActionMenu: null,
            dropdownPosition: {},
            columns: [
                { key: 'id', label: this.$t('invoices.columns.order_number'), sortable: true, slot: 'cell-id', type: 'number' },
                {
                    key: 'customer',
                    label: this.$t('invoices.columns.customer'),
                    sortable: true,
                    slot: 'cell-customer',
                    type: 'text',
                },
                {
                    key: 'articles',
                    label: this.$t('invoices.columns.articles'),
                    sortable: false,
                    slot: 'cell-articles',
                    type: 'text',
                },
                {
                    key: 'total_price',
                    label: this.$t('invoices.columns.total'),
                    sortable: true,
                    slot: 'cell-total_price',
                    type: 'number',
                },
                { key: 'status', label: this.$t('invoices.columns.status'), sortable: false, slot: 'cell-status', type: 'badge' },
                {
                    key: 'created_at',
                    label: this.$t('invoices.columns.date'),
                    sortable: true,
                    slot: 'cell-created_at',
                    type: 'date',
                },
                {
                    key: 'actions',
                    label: this.$t('invoices.columns.actions'),
                    sortable: false,
                    slot: 'cell-actions',
                    type: 'actions',
                },
            ],
        }
    },
    computed: {
        isDark() {
            return this.$vuetify.theme.dark
        },
    },
    watch: {
        startDate() {
            this.debouncedFetch()
        },
        endDate() {
            this.debouncedFetch()
        },
        filters: {
            handler() {
                this.debouncedFetch()
            },
            deep: true,
        },
    },
    mounted() {
        this.fetchOrders()
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('scroll', this.updateDropdownPosition, true)
        window.addEventListener('resize', this.updateDropdownPosition)
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('scroll', this.updateDropdownPosition, true)
        window.removeEventListener('resize', this.updateDropdownPosition)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        isWalkInCustomer(user) {
            if (!user) return true
            const fn = (user.first_name || '').trim()
            const ln = (user.last_name || '').trim()
            return (!fn || fn === '-') && (!ln || ln === '-')
        },
        debouncedFetch() {
            if (this.fetchTimer) {
                clearTimeout(this.fetchTimer)
            }
            this.fetchTimer = setTimeout(() => {
                this.currentPage = 1
                this.fetchOrders()
            }, 300)
        },
        async fetchOrders() {
            this.loading = true
            try {
                const response = await this.axios.get('/back-office/invoices-v3/drafts', {
                    params: {
                        startDate: this.startDate,
                        endDate: this.endDate,
                        search: this.localSearchQuery || this.searchQuery,
                        page: this.currentPage,
                        perPage: this.perPage,
                        ...this.filters,
                    },
                })

                const data = response.data || response
                this.orders = data.data.orders || []
                this.currentPage = parseInt(data.meta?.current_page) || 1
                this.totalPages = parseInt(data.meta?.last_page) || 1
                this.total = parseInt(data.meta?.total) || 0

                // Emit stats to parent
                this.$emit('stats-updated', {
                    count: data.data.count || 0,
                    amount: data.data.total_amount || 0,
                })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.orders = []
            } finally {
                this.loading = false
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchOrders()
        },
        handlePerPageChange(newPerPage) {
            this.perPage = parseInt(newPerPage)
            setPerPage('invoices_en_attente_items_per_page', this.perPage)
            this.currentPage = 1
            this.fetchOrders()
        },
        handleSearchUpdate(searchValue) {
            this.localSearchQuery = searchValue
            this.currentPage = 1
            this.fetchOrders()
        },
        reload() {
            this.currentPage = 1
            this.fetchOrders()
        },
        formatCurrency(amount) {
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(amount || 0)
        },
        formatDate(dateString) {
            if (!dateString) return '-'
            const mDate = moment(dateString)
            const today = moment().startOf('day')
            const yesterday = moment().subtract(1, 'days').startOf('day')

            let datePart = ''
            if (mDate.isSame(today, 'day')) {
                datePart = this.$t('dashboard.periods.today')
            } else if (mDate.isSame(yesterday, 'day')) {
                datePart = this.$t('dashboard.periods.yesterday')
            } else {
                const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
                datePart = mDate.toDate().toLocaleDateString(locale, {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                })
            }

            const timePart = mDate.format('HH:mm')
            return `${datePart} ${timePart}`
        },
        openCustomerModal(userId) {
            if (typeof window.setOrderToModal === 'function') {
                window.setOrderToModal(userId)
            } else {
                window.open(`/back-office/v3/customer/${userId}/edit`, '_blank')
            }
        },
        viewOrder(item) {
            window.location.href = `/back-office/commandes/${item.id}/descriptif`
        },
        openDeferredModal(item) {
            if (this.$refs.orderActionsDialogs) {
                this.$refs.orderActionsDialogs.openDeferred(item)
            }
        },
        requestCancelOrder(item) {
            if (this.$refs.orderActionsDialogs) {
                this.$refs.orderActionsDialogs.requestCancel(item)
            }
        },
        isPending(item) {
            return (item.payment_status?.key || item.payment_status) === 'pending'
        },
        isCancelled(item) {
            return (item.payment_status?.key || item.payment_status) === 'cancelled' || item.is_canceled || !!item.canceled_at
        },
        goToCaisse(item) {
            window.location.href = `/back-office/v3/caisse?order=${item.id}`
        },
        onMenuAction(action, item) {
            this.closeActionMenu()
            switch (action) {
                case 'view':
                    this.viewOrder(item)
                    break
                case 'modify':
                    this.goToCaisse(item)
                    break
                case 'encaisser':
                    this.goToCaisse(item)
                    break
                case 'proforma':
                    this.openDeferredModal(item)
                    break
                case 'cancel':
                    this.requestCancelOrder(item)
                    break
            }
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },
        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 220}px`, // 220 = min-width of dropdown
            }
        },
        handleClickOutside(event) {
            if (!this.activeActionMenu) return

            const actionButton = this.$refs[`actionButton-${this.activeActionMenu}`]
            const actionMenu = this.$refs[`actionMenu-${this.activeActionMenu}`]

            // Handle both array and non-array refs (Vue 2 vs Vue 3 or v-for behavior)
            const btnEl = Array.isArray(actionButton) ? actionButton[0] : actionButton
            const menuEl = Array.isArray(actionMenu) ? actionMenu[0] : actionMenu

            const clickedButton = btnEl && (btnEl === event.target || btnEl.contains(event.target))
            const clickedMenu = menuEl && (menuEl === event.target || menuEl.contains(event.target))

            if (!clickedButton && !clickedMenu) {
                this.activeActionMenu = null
            }
        },
        updateDropdownPosition() {
            if (this.activeActionMenu) {
                this.calculateDropdownPosition(this.activeActionMenu)
            }
        },
        closeActionMenu() {
            this.activeActionMenu = null
        },
        async exportData(format) {
            try {
                const params = new URLSearchParams()
                params.set('startDate', this.startDate)
                params.set('endDate', this.endDate)
                params.set('format', format)
                if (this.searchQuery) params.set('search', this.searchQuery)

                if (this.filters && typeof this.filters === 'object') {
                    Object.entries(this.filters).forEach(([key, value]) => {
                        if (value !== null && value !== undefined && value !== '') {
                            if (Array.isArray(value)) {
                                value.forEach((v) => params.append(`${key}[]`, v))
                            } else {
                                params.set(key, value)
                            }
                        }
                    })
                }

                const response = await fetch(`/back-office/invoices-v3/export-drafts?${params.toString()}`, {
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                })

                if (!response.ok) throw new Error(`${this.$t('invoices.errors.server_error')}: ${response.status}`)

                const blob = await response.blob()
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                const extension = format === 'xls' ? 'xlsx' : 'csv'
                const filename = `${this.$t('invoices.export_filename_drafts')}_${new Date()
                    .toISOString()
                    .slice(0, 10)}.${extension}`
                a.href = url
                a.download = filename
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(url)
            } catch (error) {
                this.$toast.error(this.$t('invoices.errors.export_drafts', { format, error: error.message }))
            }
        },
        getStatusLabel(item) {
            // Display-only overrides (PLPRB-2404): a credit-note (avoir) order — refund_for set —
            // shows "Avoir"; a canceled commande — is_canceled or canceled_at — shows "Annulée".
            // These take precedence over the payment-completion status.
            if (item.refund_for) {
                return this.$t('invoices.payment_status.avoir')
            }
            if (item.is_canceled || item.canceled_at) {
                return this.$t('invoices.payment_status.cancelled')
            }
            // Use label from API if available
            if (item.payment_status && item.payment_status.label) {
                return item.payment_status.label
            }
            // Fallback to local mapping
            const statusKey = item.payment_status?.key || item.payment_status
            return this.$t(`invoices.payment_status.${statusKey}`) || this.$t('invoices.payment_status.pending')
        },
        getStatusClass(item) {
            if (item.refund_for) {
                return 'ppv2-bg-purple-100 ppv2-text-purple-800'
            }
            if (item.is_canceled || item.canceled_at) {
                return 'ppv2-bg-red-100 ppv2-text-red-800'
            }
            const statusKey = item.payment_status?.key || item.payment_status
            const statusClasses = {
                cancelled: 'ppv2-bg-red-100 ppv2-text-red-800',
                paid: 'ppv2-bg-green-100 ppv2-text-green-800',
                pending: 'ppv2-bg-orange-100 ppv2-text-orange-800',
                partially_paid: 'ppv2-bg-yellow-100 ppv2-text-yellow-800',
            }
            return statusClasses[statusKey] || 'ppv2-bg-orange-100 ppv2-text-orange-800'
        },
    },
}
</script>

<style scoped lang="scss">
.actionsDropdown {
    position: fixed;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 4px 0;
}

.actionItem {
    background: transparent;
    border: none;
    color: #1a1f37;
    text-decoration: none;
    display: flex;
    align-items: center;
}

.actionItem:hover {
    background-color: #f3f4f6;
    color: #1a1f37 !important;
}

.actionItem:hover * {
    color: #1a1f37 !important;
}
.actionItem.trash:hover {
    color: #911414 !important;
    background-color: #ff9c9c10 !important;
}
.actionItem.actionItem.trash:hover * {
    color: #911414 !important;
}

.dark {
    .actionsDropdown {
        background-color: #2c2c2ce5;
    }

    .actionItem {
        color: #bfbfbf;
    }

    .actionItem:hover {
        background-color: #1a1f37;
        color: white !important;
    }

    .actionItem.trash:hover {
        color: #911414 !important;
        background-color: #ff9c9c10 !important;
    }

    .actionItem:hover * {
        color: white !important;
    }
}
</style>
